import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { products } from "./utils";

const Checkout = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // 1. Manage which currency/price the user is looking at
  const [currency, setCurrency] = useState("INR");

  const product = products.find((p) => p.id === id);

  if (!product) {
    return (
      <section style={styles.errorContainer}>
        <h2 style={{ fontSize: "2rem" }}>Oops! Product not found</h2>
        <p>The selected service does not exist or has been moved.</p>
        <button style={styles.backButton} onClick={() => navigate("/shop")}>
          Back to Shop
        </button>
      </section>
    );
  }

  const shareUrl = window.location.href;
  const currentPrice =
    currency === "INR" ? product.priceIndian : product.priceInternational;

  return (
    <section style={styles.pageWrapper}>
      <div style={styles.mainContainer}>
        {/* LEFT COLUMN: Product Info */}
        <div style={styles.productColumn}>
          <div
            style={{
              ...styles.imageHeader,
              backgroundImage: `url(../../../${product.image})`,
            }}
          />
          <div style={styles.infoCard}>
            <h1 style={styles.mainTitle}>{product.name}</h1>
            <p style={styles.longDescription}>{product.description}</p>

            <div style={styles.shareSection}>
              <span style={styles.shareLabel}>Share this service:</span>
              <div style={styles.iconGroup}>
                <SocialIcon
                  type="whatsapp"
                  url={`https://wa.me/?text=${encodeURIComponent(shareUrl)}`}
                />
                <SocialIcon
                  type="twitter"
                  url={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`}
                />
                <button
                  style={styles.copyLinkBtn}
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl);
                    alert("Link copied!");
                  }}
                >
                  Copy Link
                </button>
              </div>
            </div>

            <button style={styles.backToShop} onClick={() => navigate("/shop")}>
              Return to Catalog
            </button>
          </div>
        </div>

        {/* RIGHT COLUMN: Payment & Summary */}
        <div style={styles.paymentColumn}>
          <div style={styles.stickyCard}>
            {/* 2. Currency Switcher Tab */}
            <div style={styles.tabGroup}>
              <button
                style={{
                  ...styles.tab,
                  ...(currency === "INR" ? styles.activeTab : {}),
                }}
                onClick={() => setCurrency("INR")}
              >
                🇮🇳 Domestic
              </button>
              <button
                style={{
                  ...styles.tab,
                  ...(currency === "USD" ? styles.activeTab : {}),
                }}
                onClick={() => setCurrency("USD")}
              >
                🌎 International
              </button>
            </div>

            <div style={styles.priceTag}>
              <span style={styles.currencySymbol}>
                {currency === "INR" ? "₹" : "$"}
              </span>
              {currentPrice.toLocaleString(
                currency === "INR" ? "en-IN" : "en-US",
              )}
            </div>

            <div style={styles.summaryBox}>
              <h4 style={styles.summaryTitle}>Service Summary</h4>
              <p style={styles.summaryText}>{product.summary}</p>
            </div>

            <div style={styles.divider} />

            <h3 style={styles.sectionHeader}>Secure Payment Methods</h3>

            {/* UPI Option - Primary for INR */}
            <div style={styles.methodCard}>
              <div style={styles.methodHeader}>
                <span style={styles.methodBadge}>UPI</span>
                <h4 style={styles.methodTitle}>Scanner (GPay / PhonePe)</h4>
              </div>
              <img
                src="/qr_code.jpeg"
                alt="UPI QR"
                style={styles.qrImage}
                onError={(e) =>
                  (e.target.src =
                    "https://via.placeholder.com/150?text=QR+Code")
                }
              />
              <p style={styles.methodNote}>Best for Indian Residents</p>
            </div>

            {/* Crypto Option - Primary for International */}
            <div style={styles.methodCard}>
              <div style={styles.methodHeader}>
                <span
                  style={{
                    ...styles.methodBadge,
                    backgroundColor: "#fef3c7",
                    color: "#92400e",
                  }}
                >
                  BEP20
                </span>
                <h4 style={styles.methodTitle}>USDT / Binance Pay</h4>
              </div>
              <div style={styles.addressWrapper}>
                <code style={styles.monoAddress}>TFT3yHbcp...NXNCh</code>
                <button
                  style={styles.actionButton}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      "TFT3yHbcpKCjMV9xGFV1L7hKAdvSiNXNCh",
                    );
                    alert("Wallet address copied!");
                  }}
                >
                  Copy
                </button>
              </div>
              <p style={styles.methodNote}>Best for International Clients</p>
            </div>

            <p style={styles.disclaimer}>
              ⚠️ <strong>Strict Policy:</strong> All digital consulting sessions
              are non-refundable once payment is processed.
            </p>            
          </div>
        </div>
      </div>
    </section>
  );
};

// Helper for Social Icons
const SocialIcon = ({ type, url }) => (
  <img
    src={`/${type}.png`}
    alt={type}
    style={styles.socialIcon}
    onClick={() => window.open(url)}
    onError={(e) => (e.target.src = "https://via.placeholder.com/32?text=S")}
  />
);

const styles = {
  // ... (previous styles maintained, adding/updating below)
  pageWrapper: {
    backgroundColor: "#f4f7fa",
    minHeight: "100vh",
    padding: "40px 20px",
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  mainContainer: {
    maxWidth: "1100px",
    margin: "0 auto",
    display: "flex",
    flexWrap: "wrap",
    gap: "30px",
    alignItems: "flex-start",
  },
  productColumn: {
    flex: "1 1 500px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  paymentColumn: { flex: "1 1 380px" },
  imageHeader: {
    height: "350px",
    borderRadius: "20px",
    backgroundSize: "cover",
    backgroundPosition: "center",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
  },
  infoCard: { backgroundColor: "#fff", padding: "30px", borderRadius: "20px" },
  mainTitle: {
    fontSize: "32px",
    color: "#1a202c",
    marginBottom: "15px",
    fontWeight: "800",
  },
  longDescription: { fontSize: "16px", color: "#4a5568", lineHeight: "1.7" },

  // NEW: Tab System
  tabGroup: {
    display: "flex",
    backgroundColor: "#f1f5f9",
    padding: "4px",
    borderRadius: "12px",
    marginBottom: "20px",
  },
  tab: {
    flex: 1,
    padding: "10px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    color: "#64748b",
    background: "none",
    transition: "all 0.2s",
  },
  activeTab: {
    backgroundColor: "#fff",
    color: "#1e293b",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  },

  stickyCard: {
    backgroundColor: "#fff",
    borderRadius: "24px",
    padding: "30px",
    boxShadow: "0 20px 40px rgba(0,0,0,0.05)",
    border: "1px solid #edf2f7",
    position: "sticky",
    top: "20px",
  },
  priceTag: {
    fontSize: "42px",
    fontWeight: "900",
    color: "#2d3748",
    textAlign: "center",
    marginBottom: "15px",
  },
  currencySymbol: {
    fontSize: "24px",
    verticalAlign: "super",
    marginRight: "4px",
    color: "#7c3aed",
  },

  summaryBox: {
    backgroundColor: "#f8fafc",
    padding: "15px",
    borderRadius: "12px",
    marginBottom: "20px",
  },
  summaryTitle: {
    fontSize: "12px",
    color: "#94a3b8",
    textTransform: "uppercase",
    marginBottom: "4px",
  },
  summaryText: { fontSize: "15px", color: "#334155", fontWeight: "600" },

  divider: { height: "1px", backgroundColor: "#e2e8f0", margin: "20px 0" },
  sectionHeader: {
    fontSize: "16px",
    color: "#1e293b",
    marginBottom: "15px",
    textAlign: "center",
    fontWeight: "700",
  },

  methodCard: {
    border: "1.5px solid #f1f5f9",
    borderRadius: "16px",
    padding: "15px",
    marginBottom: "15px",
    transition: "all 0.3s ease",
  },
  methodHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
  },
  methodBadge: {
    fontSize: "10px",
    padding: "2px 8px",
    backgroundColor: "#e0e7ff",
    color: "#4338ca",
    borderRadius: "20px",
    fontWeight: "700",
  },
  methodTitle: { fontSize: "14px", fontWeight: "600", color: "#334155" },
  qrImage: {
    width: "160px",
    margin: "0 auto",
    display: "block",
    borderRadius: "10px",
  },
  methodNote: {
    fontSize: "11px",
    color: "#94a3b8",
    textAlign: "center",
    marginTop: "8px",
  },

  addressWrapper: {
    display: "flex",
    backgroundColor: "#f1f5f9",
    padding: "10px",
    borderRadius: "8px",
    alignItems: "center",
    gap: "10px",
  },
  monoAddress: {
    fontFamily: "monospace",
    fontSize: "12px",
    color: "#475569",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  actionButton: {
    backgroundColor: "#3b82f6",
    color: "#fff",
    border: "none",
    padding: "6px 12px",
    borderRadius: "6px",
    cursor: "pointer",
  },

  disclaimer: {
    fontSize: "12px",
    color: "#94a3b8",
    marginTop: "20px",
    lineHeight: "1.4",
  },
backToShop: {
    width: "100%",
    backgroundColor: "#eff6ff", // Light blue background (blue-50)
    color: "#005ddf",           // Your primary blue
    border: "2px solid #005ddf", // Solid border to match text
    borderRadius: "12px",        // Rounded corners
    padding: "14px 20px",        // Inner spacing
    fontSize: "16px",            // Balanced font size
    marginTop: "40px",
    cursor: "pointer",
    fontWeight: "700",
    transition: "all 0.3s ease", // Smooth hover effect
    display: "block",
    textAlign: "center",
  },

  shareSection: {
    marginTop: "30px",
    paddingTop: "20px",
    borderTop: "1px solid #f1f5f9",
  },
  shareLabel: {
    fontSize: "14px",
    color: "#94a3b8",
    display: "block",
    marginBottom: "10px",
  },
  iconGroup: { display: "flex", gap: "15px", alignItems: "center" },
  socialIcon: { width: "32px", height: "32px", cursor: "pointer" },
  copyLinkBtn: {
    border: "1px solid #cbd5e1",
    background: "none",
    padding: "8px 15px",
    borderRadius: "20px",
    fontSize: "13px",
    cursor: "pointer",
  },
};

export default Checkout;
